import { type MarketingLocale, useLocalized } from '~/lib/marketing-locale'
import { WhatsAppGlyph } from './marketing-glyphs'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/*
 * Reports, multi-format showcase: the same report rendered as a PDF cover, a
 * public link preview, and a messaging thread. Then a single two-column band:
 * AI draft + read receipts. No 1+3 card row.
 *
 * Copy is locale-keyed — see the note in `crm-section.tsx`. The messaging
 * panel stays WhatsApp in both languages because that is the integration
 * that exists; the English intro just puts email alongside it rather than
 * treating WhatsApp as the assumed default.
 */

const PhotoTile = ({ from, to }: { from: string; to: string }) => (
  <span
    className="block size-full rounded-md"
    style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
  />
)

type Kpi = { label: string; sub: string }
type Reader = { device: string; when: string }

type ReportsContent = {
  eyebrow: string
  heading: string
  intro: string
  studio: string
  reportEyebrow: string
  projectName: string
  reportMeta: string
  summaryLabel: string
  summary: string
  kpis: readonly Kpi[]
  pdfFileName: string
  pdfMeta: string
  linkUrl: string
  linkViews: string
  linkDevice: string
  formats: readonly [string, string, string]
  chat: {
    contact: string
    status: string
    intro: string
    fileMeta: string
    reply: string
    readAt: string
  }
  ai: { eyebrow: string; title: string; body: string }
  receipts: { eyebrow: string; title: string; rows: readonly Reader[] }
}

const CONTENT: Record<MarketingLocale, ReportsContent> = {
  en: {
    ai: {
      body: 'Decisions, deliveries, photos, open items — already in the skeleton of the document. You adjust the tone, fix what needs fixing, and send.',
      eyebrow: 'DRAFTED BY AI',
      title: 'AI pulls what moved this week and writes the report for you',
    },
    chat: {
      contact: 'The Harpers',
      fileMeta: '12 pages · 2.4 MB',
      intro: 'Morning! Sending over the May report for your review.',
      readAt: '2:32 PM · read',
      reply: 'Thank you! Just opened it — the kitchen detailing looks great 👏',
      status: 'online',
    },
    eyebrow: 'REPORTS',
    formats: ['Printable PDF', 'Public link', 'WhatsApp'],
    heading: 'One report, three formats. It goes out the way the client prefers to read.',
    intro:
      'Templates that mix text, photos, and indicators at the right rhythm. A PDF to print, a public link to share, or straight to the client by email or WhatsApp — without reformatting anything.',
    kpis: [
      { label: '64%', sub: 'scope delivered' },
      { label: '12 / 15', sub: 'tasks this month' },
      { label: '$18.2k', sub: 'invested this month' },
    ],
    linkDevice: 'Client · iPhone',
    linkUrl: 'whitfield.planno.app/r/4f2',
    linkViews: '5 views',
    pdfFileName: 'May-Report.pdf',
    pdfMeta: 'May-Report.pdf · 2.4 MB',
    projectName: 'Whitfield House',
    receipts: {
      eyebrow: 'READ RECEIPTS',
      rows: [
        { device: 'iPhone · Chicago', when: '2h ago' },
        { device: 'Safari · MacBook', when: '18h ago' },
        { device: 'Chrome · Android', when: 'yesterday' },
      ],
      title: 'You know when the client opened it, and on which device',
    },
    reportEyebrow: 'WHITFIELD STUDIO · MAY REPORT',
    reportMeta: 'Construction progress · May 2026',
    studio: 'WHITFIELD STUDIO',
    summary:
      'May closed with 12 of the 15 planned deliveries. Construction progressed on millwork detailing and plumbing coordination. Electrical approval with the engineer of record is still outstanding.',
    summaryLabel: 'SUMMARY',
  },
  'pt-BR': {
    ai: {
      body: 'Decisões, entregas, fotos, pendências, tudo já no esqueleto do documento. Você revisa o tom, ajusta o que precisa, manda.',
      eyebrow: 'RASCUNHADO POR IA',
      title: 'A IA puxa o que andou na semana e escreve o relatório por você',
    },
    chat: {
      contact: 'Família Lopes',
      fileMeta: '12 páginas · 2,4 MB',
      intro: 'Bom dia, Sra. Lopes! Mando o relatório de maio para sua leitura.',
      readAt: '14:32 · lido',
      reply: 'Obrigada! Já abri, ficou ótimo o detalhamento da cozinha 👏',
      status: 'online',
    },
    eyebrow: 'RELATÓRIOS',
    formats: ['PDF imprimível', 'Link público', 'WhatsApp'],
    heading: 'Um relatório, três formatos. Sai como o cliente prefere ler.',
    intro:
      'Templates que misturam texto, fotos e indicadores no ritmo certo. PDF para imprimir, link público para compartilhar, ou direto no WhatsApp do cliente, sem reformatar nada.',
    kpis: [
      { label: '64%', sub: 'escopo entregue' },
      { label: '12 / 15', sub: 'tarefas no mês' },
      { label: 'R$ 18,2k', sub: 'investido no mês' },
    ],
    linkDevice: 'Cliente · iPhone',
    linkUrl: 'estudio.planno.app/r/4f2',
    linkViews: '5 visualizações',
    pdfFileName: 'Relatorio-Maio.pdf',
    pdfMeta: 'Relatorio-Maio.pdf · 2,4 MB',
    projectName: 'Residência Lopes',
    receipts: {
      eyebrow: 'LEITURA CONFIRMADA',
      rows: [
        { device: 'iPhone · São Paulo', when: 'há 2h' },
        { device: 'Safari · Macbook', when: 'há 18h' },
        { device: 'Chrome · Android', when: 'ontem' },
      ],
      title: 'Você sabe quando o cliente abriu, e em qual aparelho',
    },
    reportEyebrow: 'ESTÚDIO LOPES · RELATÓRIO MAIO',
    reportMeta: 'Acompanhamento de obra · maio de 2026',
    studio: 'ESTÚDIO LOPES',
    summary:
      'O mês de maio fechou com 12 das 15 entregas previstas. A obra avançou no detalhamento de marcenaria e na compatibilização hidráulica. Ficou pendente a aprovação do projeto elétrico junto ao engenheiro responsável.',
    summaryLabel: 'RESUMO',
  },
}

const ReportMockup = ({ content }: { content: ReportsContent }) => (
  <div className="overflow-hidden rounded-3xl border border-[#ececec] bg-white shadow-[0_24px_60px_-26px_rgba(40,30,80,0.32)]">
    <div className="bg-[#1c1c20] px-6 py-4 text-white">
      <p className="text-[10.5px] text-white/60 tracking-[0.2em]">{content.reportEyebrow}</p>
      <p className="mt-2 font-bold text-[20px] leading-tight">{content.projectName}</p>
      <p className="mt-1 text-[11.5px] text-white/70">{content.reportMeta}</p>
    </div>
    <div className="p-6">
      <p className="text-[#9a9aa0] text-[10.5px] tracking-[0.18em]">{content.summaryLabel}</p>
      <p className="mt-2 text-[#43434a] text-[12.5px] leading-relaxed">{content.summary}</p>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="aspect-[4/3]">
          <PhotoTile from="#dde7f4" to="#a3bdd6" />
        </div>
        <div className="aspect-[4/3]">
          <PhotoTile from="#e7e0d2" to="#bca687" />
        </div>
        <div className="aspect-[4/3]">
          <PhotoTile from="#d8ddd3" to="#9ab09a" />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        {content.kpis.map((kpi) => (
          <div className="rounded-lg bg-[#f6f4fc] p-3" key={kpi.label}>
            <p className="font-bold text-[#1c1c20] text-[16px] leading-none">{kpi.label}</p>
            <p className="mt-1 text-[#7a6cb6] text-[10.5px]">{kpi.sub}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
)

const PdfFormat = ({ content }: { content: ReportsContent }) => (
  <div className="overflow-hidden rounded-xl border border-[#ececec] bg-white shadow-sm">
    <div className="bg-[#1c1c20] px-3 py-2 text-white">
      <p className="text-[7px] text-white/60 tracking-[0.18em]">{content.studio}</p>
      <p className="mt-0.5 font-bold text-[11px] leading-tight">{content.projectName}</p>
    </div>
    <div className="space-y-1.5 p-3">
      <span className="block h-[5px] w-[100%] rounded-full bg-[#ececec]" />
      <span className="block h-[5px] w-[90%] rounded-full bg-[#ececec]" />
      <span className="block h-[5px] w-[76%] rounded-full bg-[#ececec]" />
      <div className="my-2 grid grid-cols-2 gap-1">
        <div className="aspect-[4/3]">
          <PhotoTile from="#dde7f4" to="#a3bdd6" />
        </div>
        <div className="aspect-[4/3]">
          <PhotoTile from="#e7e0d2" to="#bca687" />
        </div>
      </div>
      <span className="block h-[5px] w-[100%] rounded-full bg-[#ececec]" />
      <span className="block h-[5px] w-[64%] rounded-full bg-[#ececec]" />
    </div>
    <div className="flex items-center justify-between border-[#f1f1f1] border-t px-3 py-2">
      <span className="font-bold text-[#1c1c20] text-[10px]">PDF</span>
      <span className="text-[#9a9aa0] text-[9px]">{content.pdfMeta}</span>
    </div>
  </div>
)

const LinkFormat = ({ content }: { content: ReportsContent }) => (
  <div className="overflow-hidden rounded-xl border border-[#e7e3f4] bg-white shadow-sm">
    <div className="flex items-center gap-1.5 border-[#f1f1f1] border-b bg-[#fafafa] px-3 py-2 text-[#9a9aa0] text-[9px]">
      <span className="size-[7px] rounded-full bg-[#e0593f]" />
      <span className="size-[7px] rounded-full bg-[#e8a13d]" />
      <span className="size-[7px] rounded-full bg-[#7bb86b]" />
      <span className="ml-2 truncate text-[#43434a]">{content.linkUrl}</span>
    </div>
    <div className="bg-gradient-to-br from-[#f6f4fc] to-white px-3 py-2 text-[#7a6cb6]">
      <p className="text-[8.5px] tracking-[0.18em]">{content.studio}</p>
      <p className="font-bold text-[#1c1c20] text-[11px] leading-tight">{content.projectName}</p>
    </div>
    <div className="space-y-1.5 p-3">
      <span className="block h-[5px] w-[100%] rounded-full bg-[#ececec]" />
      <span className="block h-[5px] w-[82%] rounded-full bg-[#ececec]" />
      <div className="my-2 grid grid-cols-3 gap-1">
        <div className="aspect-square">
          <PhotoTile from="#dde7f4" to="#a3bdd6" />
        </div>
        <div className="aspect-square">
          <PhotoTile from="#e7e0d2" to="#bca687" />
        </div>
        <div className="aspect-square">
          <PhotoTile from="#d8ddd3" to="#9ab09a" />
        </div>
      </div>
      <div className="flex items-center justify-between text-[9px]">
        <span className="text-[#9a9aa0]">{content.linkViews}</span>
        <span className="text-[#7a6cb6]">{content.linkDevice}</span>
      </div>
    </div>
  </div>
)

const WhatsAppFormat = ({ content }: { content: ReportsContent }) => (
  <div className="overflow-hidden rounded-xl border border-[#dfeedb] bg-[#e5ddd5] shadow-sm">
    <div className="flex items-center gap-2 bg-[#075e54] px-3 py-2 text-white">
      <span className="flex size-6 items-center justify-center rounded-full bg-white/20">
        <WhatsAppGlyph className="size-3.5" />
      </span>
      <div>
        <p className="font-medium text-[10.5px]">{content.chat.contact}</p>
        <p className="text-[8.5px] text-white/70">{content.chat.status}</p>
      </div>
    </div>
    <div className="space-y-2 p-3">
      <div className="ml-auto max-w-[88%] rounded-2xl rounded-br-sm bg-[#dcf8c6] px-2.5 py-2">
        <p className="text-[#1c1c20] text-[10px] leading-snug">{content.chat.intro}</p>
      </div>
      <div className="ml-auto max-w-[92%] rounded-2xl rounded-br-sm bg-[#dcf8c6] p-2.5">
        <div className="rounded-md bg-white/60 p-2">
          <p className="font-medium text-[#1c1c20] text-[10px]">📄 {content.pdfFileName}</p>
          <p className="mt-0.5 text-[#5e6f5e] text-[9px]">{content.chat.fileMeta}</p>
        </div>
      </div>
      <div className="max-w-[80%] rounded-2xl rounded-bl-sm bg-white px-2.5 py-2">
        <p className="text-[#1c1c20] text-[10px] leading-snug">{content.chat.reply}</p>
        <p className="text-right text-[#9a9aa0] text-[8px]">{content.chat.readAt}</p>
      </div>
    </div>
  </div>
)

export const ReportsSection = () => {
  const content = useLocalized(CONTENT)

  const formats = [
    { Comp: PdfFormat, format: content.formats[0], tone: '#1c1c20' },
    { Comp: LinkFormat, format: content.formats[1], tone: '#7a6cb6' },
    { Comp: WhatsAppFormat, format: content.formats[2], tone: '#25d366' },
  ]

  return (
    <section
      className="relative overflow-hidden py-28"
      style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f4f0fb 40%, #ece8f9 100%)' }}
    >
      <TruchetField
        className="absolute inset-0 h-full w-full text-[#0b0b0d] opacity-[0.05]"
        seed="reports"
      />
      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <p className="text-[#7a6cb6] text-[12px] tracking-[0.2em]">{content.eyebrow}</p>
          <h2 className="mx-auto mt-3 max-w-[820px] font-display text-[#0b0b0d] text-[clamp(40px,6vw,58px)] leading-[1.06] tracking-[-0.01em]">
            {content.heading}
          </h2>
          <p className="mx-auto mt-5 max-w-[640px] text-[#6c6c72] text-[clamp(16px,2.2vw,20px)]">
            {content.intro}
          </p>
        </ScrollReveal>

        <ScrollReveal className="mt-14" delay={100}>
          <ReportMockup content={content} />
        </ScrollReveal>

        {/* Three format previews, each labeled with its delivery channel */}
        <div className="mt-10 grid gap-6 sm:grid-cols-3">
          {formats.map(({ format, Comp, tone }, i) => (
            <ScrollReveal delay={i * 100} key={format}>
              <div>
                <div className="flex items-center gap-2 pb-3">
                  <span className="size-[8px] rounded-full" style={{ background: tone }} />
                  <span className="font-semibold text-[#43434a] text-[12px] tracking-[0.14em]">
                    {format.toUpperCase()}
                  </span>
                </div>
                <Comp content={content} />
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* AI + read receipts, single two-column band, not three cards */}
        <ScrollReveal className="mt-12" delay={140}>
          <div className="grid items-stretch gap-0 overflow-hidden rounded-2xl border border-[#e7e3f4] bg-white md:grid-cols-2">
            <div className="p-7">
              <p className="text-[#7a6cb6] text-[11px] tracking-[0.2em]">{content.ai.eyebrow}</p>
              <h3 className="mt-2 font-semibold text-[#0b0b0d] text-[20px]">{content.ai.title}</h3>
              <p className="mt-3 text-[#6c6c72] text-[14.5px] leading-relaxed">{content.ai.body}</p>
            </div>
            <div className="bg-[#f6f4fc] p-7">
              <p className="text-[#7a6cb6] text-[11px] tracking-[0.2em]">
                {content.receipts.eyebrow}
              </p>
              <h3 className="mt-2 font-semibold text-[#0b0b0d] text-[20px]">
                {content.receipts.title}
              </h3>
              <div className="mt-4 space-y-2">
                {content.receipts.rows.map((r) => (
                  <div
                    className="flex items-center justify-between rounded-lg bg-white px-3 py-2"
                    key={r.device}
                  >
                    <span className="text-[#43434a] text-[12px]">{r.device}</span>
                    <span className="text-[#7a6cb6] text-[11px]">{r.when}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  )
}
